import { Posting } from '../models/posting.model';
import { PostingState } from '../states/posting.state';
import { Actions, ofActionDispatched, Select, Store } from '@ngxs/store';
import {
    CreatePosting,
    CreatePostingFailure,
    CreatePostingFromArticle,
    CreatePostingFromArticleFailure,
    CreatePostingFromArticleSuccess,
    CreatePostingSuccess,
    DeletePosting,
    DeletePostingFailure,
    DeletePostingSuccess,
    FetchEngageCategories,
    FetchEngageCategoriesFailure,
    FetchEngageCategoriesSuccess,
    FetchEngageCategoriesWithPostings,
    FetchEngageCategoriesWithPostingsFailure,
    FetchEngageCategoriesWithPostingsSuccess,
    FetchPosting,
    FetchPostingFailure,
    FetchPostings,
    FetchPostingsFailure,
    FetchPostingsSuccess,
    FetchPostingSuccess,
    UpdatePostingFromArticle,
    UpdatePostingFromArticleFailure,
    UpdatePostingFromArticleSuccess,
} from '../actions';
import { Injectable } from '@angular/core';
import { SortByAllPostings, SortLanguageAllPostings, SortOrderAllPostings } from '../actions/all-postings.action';
import { Observable, race } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { NgxsActionHelper } from 'app/projects/core/src/lib/services/action.helper';
import { IEngageCategoryFilters } from '../interfaces/engage-category-filters.interface';
import { EngageCategory } from '../models/engage-category.model';
import { EngageCategoryWithPosting } from '../models/engage-category-with-posting.model';

@Injectable({
    providedIn: 'root',
})
export class PostingService {
    @Select(PostingState.getEntities)
    postings$: Observable<Posting[]>;

    @Select(PostingState.getEntity)
    currentPosting: Observable<Posting>;

    tempSortBySelectedValue: any;
    tempSortOrderSelectedValue: any;

    constructor(private _store: Store, private _action$: Actions, private _actionHelper: NgxsActionHelper) {}

    /**
     *
     */
    getCurrentPosting(): Posting {
        return this._store.selectSnapshot(PostingState.getEntity);
    }

    /**
     *
     */
    getPosting(): Posting[] {
        return this._store.selectSnapshot(PostingState.getEntities);
    }

    /**
     *
     * @param postingId
     */
    getPostingById(postingId: string): Posting {
        return PostingState.getEntity(this._store.selectSnapshot(PostingState), postingId);
    }

    /**
     *
     */
    async fetchPostings(): Promise<boolean> {
        this._store.dispatch(new FetchPostings());

        return await this._actionHelper.race(FetchPostingsSuccess, FetchPostingsFailure).toPromise();
    }

    /**
     *
     * @param postingId
     * @param force
     */
    async fetchPostingById(postingId: string, force: boolean = false): Promise<boolean> {
        if (!force) {
            const currentPosting = this.getCurrentPosting();

            if (currentPosting && currentPosting.id === postingId) {
                return true;
            }
        }

        this._store.dispatch(new FetchPosting(postingId));

        return await this._actionHelper.race(FetchPostingSuccess, FetchPostingFailure).toPromise();
    }

    deletePosting(postingId: string): Promise<boolean> {
        this._store.dispatch(new DeletePosting(postingId));

        return race(
            this._action$.pipe(
                ofActionDispatched(DeletePostingSuccess),
                map((action: DeletePostingSuccess) => true)
            ),
            this._action$.pipe(
                ofActionDispatched(DeletePostingFailure),
                map((action: DeletePostingFailure) => false)
            )
        )
            .pipe(take(1))
            .toPromise();
    }

    async createPosting(posting: Posting): Promise<boolean> {
        this._store.dispatch(new CreatePosting(posting));

        return await this._actionHelper.race(CreatePostingSuccess, CreatePostingFailure).toPromise();
    }

    async updatePostingFromArticle(posting: Posting): Promise<Posting> {
        this._store.dispatch(new UpdatePostingFromArticle(posting));

        return race(
            this._action$.pipe(
                ofActionDispatched(UpdatePostingFromArticleSuccess),
                map((action: UpdatePostingFromArticleSuccess) => action.payload)
            ),
            this._action$.pipe(
                ofActionDispatched(UpdatePostingFromArticleFailure),
                map((action: UpdatePostingFromArticleFailure) => null)
            )
        )
            .pipe(take(1))
            .toPromise();
    }

    async createPostingFromArticle(posting: Posting): Promise<Posting> {
        this._store.dispatch(new CreatePostingFromArticle(posting));

        return race(
            this._action$.pipe(
                ofActionDispatched(CreatePostingFromArticleSuccess),
                map((action: CreatePostingFromArticleSuccess) => action.payload)
            ),
            this._action$.pipe(
                ofActionDispatched(CreatePostingFromArticleFailure),
                map((action: CreatePostingFromArticleFailure) => null)
            )
        )
            .pipe(take(1))
            .toPromise();
    }

    fetchEngageCategories(audienceId: string, searchTerm?: string, postingTypeId?: number, filters?: IEngageCategoryFilters): Promise<EngageCategory[]> {
        this._store.dispatch(new FetchEngageCategories({ audienceId, searchTerm, postingTypeId, ...filters }));

        return race(
            this._action$.pipe(
                ofActionDispatched(FetchEngageCategoriesSuccess),
                map((action: FetchEngageCategoriesSuccess) => action.payload)
            ),
            this._action$.pipe(
                ofActionDispatched(FetchEngageCategoriesFailure),
                map((action: FetchEngageCategoriesFailure) => null)
            )
        )
            .pipe(take(1))
            .toPromise();
    }

    fetchEngageCategoriesWithPostings(audienceId: string, filters?: IEngageCategoryFilters): Promise<EngageCategoryWithPosting[]> {
        this._store.dispatch(new FetchEngageCategoriesWithPostings({ audienceId, ...filters }));

        return race(
            this._action$.pipe(
                ofActionDispatched(FetchEngageCategoriesWithPostingsSuccess),
                map((action: FetchEngageCategoriesWithPostingsSuccess) => action.payload)
            ),
            this._action$.pipe(
                ofActionDispatched(FetchEngageCategoriesWithPostingsFailure),
                map((action: FetchEngageCategoriesWithPostingsFailure) => null)
            )
        )
            .pipe(take(1))
            .toPromise();
    }

    async setSortBy(payload): Promise<any> {
        return this._store.dispatch(new SortByAllPostings(payload));
    }

    async setSortOder(payload): Promise<any> {
        return this._store.dispatch(new SortOrderAllPostings(payload));
    }

    async setSortLanguage(payload): Promise<any> {
        return this._store.dispatch(new SortLanguageAllPostings(payload));
    }
}
